import React, { useRef, useEffect, useState } from 'react';
import { AudioMapping } from './ShaderParameterExtractor';

interface ShadertoyRendererProps {
  audioData: {
    bass: number;
    mid: number;
    treble: number;
    volume: number;
  };
  isPlaying: boolean;
  shaderCode?: string;
  customParameters?: Record<string, number | number[]>;
  audioMappings?: AudioMapping[];
  audioContextRef?: React.MutableRefObject<AudioContext | undefined>;
  audioChannels?: Record<string, 'audio' | 'texture' | 'disabled'>;
}

interface ShadertoyUniforms {
  iResolution: WebGLUniformLocation | null;
  iTime: WebGLUniformLocation | null;
  iTimeDelta: WebGLUniformLocation | null;
  iFrame: WebGLUniformLocation | null;
  iMouse: WebGLUniformLocation | null;
  iDate: WebGLUniformLocation | null;
  // Audio extensions
  iAudioBass: WebGLUniformLocation | null;
  iAudioMid: WebGLUniformLocation | null;
  iAudioTreble: WebGLUniformLocation | null;
  iAudioVolume: WebGLUniformLocation | null;
  // Custom parameters
  [key: string]: WebGLUniformLocation | null;
}

const ShadertoyRenderer: React.FC<ShadertoyRendererProps> = ({ 
  audioData, 
  isPlaying, 
  shaderCode,
  customParameters = {},
  audioMappings = [],
  audioContextRef,
  audioChannels = {
    iChannel0: 'audio',
    iChannel1: 'audio', 
    iChannel2: 'disabled',
    iChannel3: 'disabled'
  }
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGL2RenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const uniformsRef = useRef<ShadertoyUniforms | null>(null);
  const animationRef = useRef<number>();
  const [startTime] = useState(Date.now());
  const [frameCount, setFrameCount] = useState(0);
  const [lastTime, setLastTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isWebGL2, setIsWebGL2] = useState(false);

  // Simple test shader to verify conversion
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testShaderCode = `
void mainImage( out vec4 fragColor, in vec2 fragCoord )
{
    vec2 uv = fragCoord.xy / iResolution.xy;
    vec3 col = vec3(uv.x, uv.y, 0.5);
    col *= (1.0 + iAudioBass);
    fragColor = vec4(col, 1.0);
}
`;

  // Default Shadertoy shader (the one you provided)
  const defaultShaderCode = `
#define A 9. // amplitude
#define T (iTime/3e2)
#define H(a) (cos(radians(vec3(180, 90, 0))+(a)*6.2832)*.5+.5)  // hue

float map(vec3 u, float v)  // sdf
{
    float t = T,     // speed
          l = 5.,    // loop to reduce clipping
          f = 1e10, i = 0., y, z;
    
    u.xy = vec2(atan(u.x, u.y), length(u.xy));  // polar transform
    u.x += t*v*3.1416*.7;  // counter rotation
    
    for (; i++<l;)
    {
        vec3 p = u;
        y = round((p.y-i)/l)*l+i;
        p.x *= y;
        p.x -= y*y*t*3.1416;
        p.x -= round(p.x/6.2832)*6.2832;
        p.y -= y;
        z = cos(y*t*6.2832)*.5 +.5;  // z wave
        f = min(f, max(length(p.xy), -p.z -z*A) -.1 -z*.2 -p.z/1e2);  // tubes
    }
    return f;
}

void mainImage( out vec4 C, vec2 U )
{
    vec2 R = iResolution.xy, j,
         M = iMouse.xy,
         m = (M -R/2.)/R.y;
    
    if (iMouse.z < 1. && M.x+M.y < 10.) m = vec2(0, .5);
    
    vec3 o = vec3(0, 0, -130.),  // camera
         u = normalize(vec3(U -R/2., R.y)),  // 3d coords
         c = vec3(0),
         p, k;
    
    float t = T,
          v = -o.z/3.,  // pattern scale
          i = 0., d = i,
          s, f, z, r;
    
    bool b;
    
    for (; i++<70.;)  // raymarch
    {
        p = u*d +o;
        p.xy /= v;           // scale down
        r = length(p.xy);    // radius
        z = abs(1. -r*r);    // z warp
        b = r < 1.;          // inside?
        if (b) z = sqrt(z);
        p.xy /= z+1.;        // spherize
        p.xy -= m;           // move with mouse
        p.xy *= v;           // scale back up
        p.xy -= cos(p.z/8. +t*3e2 +vec2(0, 1.5708) +z/2.)*.2;  // wave along z
        
        s = map(p, v);  // sdf
        
        r = length(p.xy);                  // new r
        f = cos(round(r)*t*6.2832)*.5+.5;  // multiples
        k = H(.2 -f/3. +t +p.z/2e2);       // color
        if (b) k = 1.-k;                   // flip color
        
        // this stuff can go outside the raymarch,
        // but accumulating it here produces softer edges
        c += min(exp(s/-.05), s)        // shapes
           * (f+.01)                    // shade pattern
           * min(z, 1.)                 // darken edges
           * sqrt(cos(r*6.2832)*.5 +.5) // shade between rows
           * k*k;                       // color
        
        if (s < 1e-3 || d > 1e3) break;
        d += s*clamp(z, .3, .9);  // smaller steps towards sphere edge
    }
    
    // c += texture(iChannel0, u*d +o).rrr * vec3(0, .4, s)*s*z*.03;  // wavy aqua (disabled - no texture)
    c += min(exp(-p.z -f*A)*z*k*.01/s, 1.);  // light tips
        
    j = p.xy/v +m;  // 2d coords
    c /= clamp(dot(j, j)*4., .04, 4.);  // brightness
    
    // Audio enhancement - VERY dramatic for testing
    c *= (1.0 + iAudioBass * 3.0);
    c += vec3(iAudioMid * 2.0, iAudioTreble * 2.0, iAudioVolume * 1.0);
    
    // Add pulsing background effect
    c += sin(iTime * 10.0) * iAudioBass * 0.5;
    
    C = vec4(exp(log(c)/2.2), 1);
}
`;

  // Convert Shadertoy code to WebGL-compatible fragment shader
  const convertShadertoyCode = (code: string): string => {
    let convertedCode = code;
    
    // Remove #define statements for parameters that we have uniforms for
    // But keep preprocessor directives that are used in #if statements
    const preprocessorUsed = new Set<string>();
    
    // Find all #if, #ifdef, #ifndef uses to preserve those defines
    const ifMatches = convertedCode.match(/#if\w*\s+([A-Z_][A-Z0-9_]*)/g);
    if (ifMatches) {
      ifMatches.forEach(match => {
        const varMatch = match.match(/#if\w*\s+([A-Z_][A-Z0-9_]*)/);
        if (varMatch) {
          preprocessorUsed.add(varMatch[1]);
        }
      });
    }
    
    Object.keys(customParameters).forEach(paramName => {
      // Don't remove defines that are used in preprocessor directives
      if (!preprocessorUsed.has(paramName)) {
        const defineRegex = new RegExp(`#define\\s+${paramName}\\s+[0-9.-]+`, 'g');
        convertedCode = convertedCode.replace(defineRegex, `// #define ${paramName} replaced with uniform`);
      }
    });
    
    // Handle texture lookups - map iChannels based on configuration
    for (let channel = 0; channel <= 3; channel++) {
      const channelName = `iChannel${channel}`;
      const channelType = audioChannels[channelName] || 'disabled';
      
      if (channelType === 'audio') {
        // Frequency-based audio sampling (common pattern in audio visualizers)
        convertedCode = convertedCode.replace(
          new RegExp(`texture\\s*\\(\\s*iChannel${channel}\\s*,\\s*vec2\\s*\\(\\s*([^,]+)\\s*,\\s*[^)]+\\)\\s*\\)`, 'g'),
          `vec4(mix(iAudioBass, mix(iAudioMid, iAudioTreble, clamp($1, 0.0, 1.0)), clamp($1 * 2.0, 0.0, 1.0)))`
        );
        
        // Simple iChannel references - map to full audio data
        convertedCode = convertedCode.replace(
          new RegExp(`texture\\s*\\(\\s*iChannel${channel}[^)]*\\)`, 'g'), 
          'vec4(iAudioVolume, iAudioBass, iAudioMid, iAudioTreble)'
        );
        
        // Handle custom functions like texNoise that take iChannel as parameter
        convertedCode = convertedCode.replace(
          new RegExp(`\\btexNoise\\s*\\([^,]+,\\s*iChannel${channel}\\s*\\)`, 'g'),
          'vec4(iAudioVolume * 0.5 + 0.05)'
        );
        
        // Replace standalone iChannel references with audio data
        convertedCode = convertedCode.replace(
          new RegExp(`\\biChannel${channel}\\b`, 'g'),
          'vec4(iAudioVolume, iAudioBass, iAudioMid, iAudioTreble)'
        );
      } else if (channelType === 'texture') {
        // Future: could load actual textures, for now use a pattern
        convertedCode = convertedCode.replace(
          new RegExp(`texture\\s*\\(\\s*iChannel${channel}[^)]*\\)`, 'g'), 
          'vec4(0.5, 0.5, 0.5, 1.0)'  // Gray placeholder
        );
        
        // Handle custom functions like texNoise that take iChannel as parameter
        convertedCode = convertedCode.replace(
          new RegExp(`\\btexNoise\\s*\\([^,]+,\\s*iChannel${channel}\\s*\\)`, 'g'),
          'vec4(0.3)'
        );
        
        // Replace standalone iChannel references with gray
        convertedCode = convertedCode.replace(
          new RegExp(`\\biChannel${channel}\\b`, 'g'),
          'vec4(0.5, 0.5, 0.5, 1.0)'
        );
      } else {
        // Disabled - return black, handle different texture() call patterns
        // Handle texture calls with bias parameter: texture(iChannel, uv, bias)
        convertedCode = convertedCode.replace(
          new RegExp(`texture\\s*\\(\\s*iChannel${channel}\\s*,\\s*[^,]+\\s*,\\s*[^)]+\\)`, 'g'), 
          'vec4(0.0, 0.0, 0.0, 1.0)'
        );
        // Handle simple texture calls: texture(iChannel, uv)
        convertedCode = convertedCode.replace(
          new RegExp(`texture\\s*\\(\\s*iChannel${channel}\\s*,\\s*[^)]+\\)`, 'g'), 
          'vec4(0.0, 0.0, 0.0, 1.0)'
        );
        
        // Handle custom functions like texNoise that take iChannel as parameter
        convertedCode = convertedCode.replace(
          new RegExp(`\\btexNoise\\s*\\([^,]+,\\s*iChannel${channel}\\s*\\)`, 'g'),
          'vec4(0.0)'
        );
        
        // Replace standalone iChannel references with black
        convertedCode = convertedCode.replace(
          new RegExp(`\\biChannel${channel}\\b`, 'g'),
          'vec4(0.0, 0.0, 0.0, 1.0)'
        );
      }
    }
    
    // console.log('Audio channel mapping:', audioChannels);
    
    // Extract parameter names from mainImage signature
    const mainImageMatch = convertedCode.match(/void\s+mainImage\s*\(\s*out\s+vec4\s+(\w+)\s*,\s*(?:in\s+)?vec2\s+(\w+)\s*\)/);
    
    if (mainImageMatch) {
      const fragColorName = mainImageMatch[1];
      const fragCoordName = mainImageMatch[2];
      
      // Replace the mainImage signature with main()
      convertedCode = convertedCode.replace(
        /void\s+mainImage\s*\(\s*out\s+vec4\s+\w+\s*,\s*(?:in\s+)?vec2\s+\w+\s*\)/g,
        'void main()'
      );
      
      // Find all function declarations to avoid replacing their names
      const functionDeclarations = new Set<string>();
      const funcRegex = /(?:vec[234]|float|int|bool)\s+(\w+)\s*\(/g;
      let funcMatch;
      while ((funcMatch = funcRegex.exec(convertedCode)) !== null) {
        functionDeclarations.add(funcMatch[1]);
      }
      
      // Also find function calls to avoid replacing
      const functionCalls = new Set<string>();
      const callRegex = /(\w+)\s*\(/g;
      let callMatch;
      while ((callMatch = callRegex.exec(convertedCode)) !== null) {
        functionCalls.add(callMatch[1]);
      }
      
      // Only replace if it's not a function name
      if (!functionDeclarations.has(fragColorName) && !functionCalls.has(fragColorName)) {
        convertedCode = convertedCode.replace(new RegExp(`\\b${fragColorName}\\b`, 'g'), 'fragColor');
      }
      
      if (!functionDeclarations.has(fragCoordName) && !functionCalls.has(fragCoordName)) {
        // Handle fragCoord replacement more carefully to avoid .xy.xy issues
        // Use a more specific approach to avoid conflicts
        
        // First, find all function signatures and temporarily replace the parameter names
        const functionSignatures = [];
        const funcSigRegex = /\b\w+\s*\([^)]*\)/g;
        let match;
        while ((match = funcSigRegex.exec(convertedCode)) !== null) {
          functionSignatures.push({
            original: match[0],
            start: match.index,
            end: match.index + match[0].length
          });
        }
        
        // Process function signatures to replace parameter names
        functionSignatures.reverse().forEach(sig => {
          const paramPattern = new RegExp(`\\b(in|out|inout)\\s+vec2\\s+${fragCoordName}\\b`, 'g');
          const newSig = sig.original.replace(paramPattern, '$1 vec2 fragCoord');
          convertedCode = convertedCode.substring(0, sig.start) + newSig + convertedCode.substring(sig.end);
        });
        
        // Now replace fragCoord usage in the body (not in function signatures)
        // But avoid replacing when it's being assigned to (left-hand side of assignment)
        
        // First, find all assignment patterns and temporarily replace them
        const assignments: Array<{original: string, placeholder: string, start: number, end: number}> = [];
        const assignmentPattern = new RegExp(`\\b${fragCoordName}(\\.[xy]{1,2})?\\s*=`, 'g');
        let assignMatch;
        while ((assignMatch = assignmentPattern.exec(convertedCode)) !== null) {
          const placeholder: string = `__TEMP_ASSIGN_${assignments.length}__`;
          assignments.push({
            original: assignMatch[0],
            placeholder: placeholder,
            start: assignMatch.index,
            end: assignMatch.index + assignMatch[0].length
          });
        }
        
        // Replace assignments with placeholders (reverse order to maintain indices)
        assignments.reverse().forEach(assignment => {
          convertedCode = convertedCode.substring(0, assignment.start) + assignment.placeholder + convertedCode.substring(assignment.end);
        });
        
        // Now safely replace fragCoord usage (not assignments)
        // Replace fragCoord.xy with gl_FragCoord.xy
        convertedCode = convertedCode.replace(new RegExp(`\\b${fragCoordName}\\.xy\\b`, 'g'), 'gl_FragCoord.xy');
        
        // Replace fragCoord.x with gl_FragCoord.x
        convertedCode = convertedCode.replace(new RegExp(`\\b${fragCoordName}\\.x\\b`, 'g'), 'gl_FragCoord.x');
        
        // Replace fragCoord.y with gl_FragCoord.y
        convertedCode = convertedCode.replace(new RegExp(`\\b${fragCoordName}\\.y\\b`, 'g'), 'gl_FragCoord.y');
        
        // Finally replace standalone fragCoord with gl_FragCoord.xy
        convertedCode = convertedCode.replace(new RegExp(`\\b${fragCoordName}(?![\\w.])`, 'g'), 'gl_FragCoord.xy');
        
        // Restore assignments with original names (but don't convert to gl_FragCoord)
        assignments.reverse().forEach(assignment => {
          convertedCode = convertedCode.replace(assignment.placeholder, assignment.original);
        });
      }
    }
    
    // Handle common single-letter variable names used in compact shaders
    // Check if they're functions first
    const hasUFunction = /\bU\s*\(/.test(convertedCode);
    const hasCFunction = /\bC\s*\(/.test(convertedCode);
    
    if (!hasUFunction) {
      convertedCode = convertedCode.replace(/\bU\b/g, 'gl_FragCoord.xy');
    }
    if (!hasCFunction) {
      convertedCode = convertedCode.replace(/\bC\b/g, 'fragColor');
    }
    
    // Additional safety checks for common shader issues
    // Remove any remaining gl_ prefixed variables that might cause conflicts
    convertedCode = convertedCode.replace(/\bgl_FragCoord\.xy\.xy\b/g, 'gl_FragCoord.xy');
    convertedCode = convertedCode.replace(/\bgl_FragCoord\.x\.x\b/g, 'gl_FragCoord.x');
    convertedCode = convertedCode.replace(/\bgl_FragCoord\.y\.y\b/g, 'gl_FragCoord.y');
    
    // Fix any malformed gl_ references that might have been created
    convertedCode = convertedCode.replace(/\bgl_\.xy\b/g, 'gl_FragCoord.xy');
    convertedCode = convertedCode.replace(/\bgl_\.x\b/g, 'gl_FragCoord.x');
    convertedCode = convertedCode.replace(/\bgl_\.y\b/g, 'gl_FragCoord.y');
    convertedCode = convertedCode.replace(/\bgl_\b/g, 'gl_FragCoord.xy');
    
    // Clean up any remaining invalid gl_ patterns
    convertedCode = convertedCode.replace(/\bgl_[^A-Za-z_]/g, 'gl_FragCoord');
    convertedCode = convertedCode.replace(/\bgl_$/g, 'gl_FragCoord.xy');
    
    return convertedCode;
  };

  // Create full fragment shader with Shadertoy uniforms
  const createFragmentShader = (userCode: string): string => {
    const convertedCode = convertShadertoyCode(userCode);
    
    // Generate custom parameter uniforms
    const customUniforms = Object.keys(customParameters).map(paramName => {
      const value = customParameters[paramName];
      if (Array.isArray(value)) {
        const size = value.length;
        const type = size === 2 ? 'vec2' : size === 3 ? 'vec3' : 'vec4';
        return `uniform ${type} ${paramName};`;
      } else {
        return `uniform float ${paramName};`;
      }
    }).join('\n');
    
    const fullShader = `#version 300 es
precision highp float;

// Shadertoy uniforms
uniform vec3 iResolution;
uniform float iTime;
uniform float iTimeDelta;
uniform int iFrame;
uniform vec4 iMouse;
uniform vec4 iDate;

// Audio uniforms
uniform float iAudioBass;
uniform float iAudioMid;
uniform float iAudioTreble;
uniform float iAudioVolume;

// Custom parameters
${customUniforms}

out vec4 fragColor;

${convertedCode}
`;
    
    // Debug: Always log the generated shader code for debugging
    console.log('=== GENERATED SHADER CODE ===');
    console.log(fullShader);
    console.log('=== END SHADER CODE ===');
    
    // Debug: log the generated shader code when there are issues
    if (fullShader.includes('gl_FragCoord.xy.xy') || fullShader.includes('gl_.')) {
      console.log('Generated shader code with potential issues:', fullShader);
    }
    return fullShader;
  };

  // Vertex shader for WebGL 2.0
  const vertexShaderSource = `#version 300 es
precision highp float;

in vec2 a_position;

void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

  const createShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      console.error('Shader compilation error:', error);
      
      // Extract line number from error message
      if (error) {
        const lineMatch = error.match(/ERROR: 0:(\d+):/);
        if (lineMatch) {
          const lineNum = parseInt(lineMatch[1]);
          const lines = source.split('\n');
          const problemLine = lines[lineNum - 1];
          console.error(`Problem on line ${lineNum}:`, problemLine);
          console.error('Context around line:', lines.slice(Math.max(0, lineNum - 3), lineNum + 2));
        }
      }
      
      console.error('Full shader source:', source);
      setError(`Shader compilation error: ${error}\n\nThis might be due to function name conflicts in the Shadertoy code. Try using a simpler shader or check for duplicate function names.`);
      gl.deleteShader(shader);
      return null;
    }
    
    return shader;
  };

  const createProgram = (gl: WebGL2RenderingContext, vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null => {
    const program = gl.createProgram();
    if (!program) return null;
    
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const error = gl.getProgramInfoLog(program);
      console.error('Program linking error:', error);
      setError(`Program linking error: ${error}`);
      gl.deleteProgram(program);
      return null;
    }
    
    return program;
  };

  const initWebGL = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not available');
      return;
    }

    // Try WebGL 2.0 first
    let gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
    if (gl) {
      setIsWebGL2(true);
      // console.log('ShadertoyRenderer: WebGL 2.0 context initialized');
    } else {
      setError('WebGL 2.0 not supported - Shadertoy requires WebGL 2.0');
      return;
    }

    glRef.current = gl;
    setError(null);

    // Create shaders
    const fragmentShaderCode = createFragmentShader(shaderCode || defaultShaderCode);
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderCode);
    
    if (!vertexShader || !fragmentShader) return;

    // Create program
    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) return;

    programRef.current = program;

    // Create buffer for full-screen quad
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1
    ]), gl.STATIC_DRAW);

    // Setup vertex attributes
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const uniforms: ShadertoyUniforms = {
      iResolution: gl.getUniformLocation(program, 'iResolution'),
      iTime: gl.getUniformLocation(program, 'iTime'),
      iTimeDelta: gl.getUniformLocation(program, 'iTimeDelta'),
      iFrame: gl.getUniformLocation(program, 'iFrame'),
      iMouse: gl.getUniformLocation(program, 'iMouse'),
      iDate: gl.getUniformLocation(program, 'iDate'),
      iAudioBass: gl.getUniformLocation(program, 'iAudioBass'),
      iAudioMid: gl.getUniformLocation(program, 'iAudioMid'),
      iAudioTreble: gl.getUniformLocation(program, 'iAudioTreble'),
      iAudioVolume: gl.getUniformLocation(program, 'iAudioVolume'),
    };

    // Get custom parameter uniform locations
    Object.keys(customParameters).forEach(paramName => {
      if (gl && program) {
        uniforms[paramName] = gl.getUniformLocation(program, paramName);
      }
    });

    uniformsRef.current = uniforms;

    gl.useProgram(program);

    // Clear canvas to black initially
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // console.log('ShadertoyRenderer: Shader program created successfully');
  };

  const render = () => {
    const gl = glRef.current;
    const program = programRef.current;
    const canvas = canvasRef.current;
    const uniforms = uniformsRef.current;
    
    if (!gl || !program || !canvas || !uniforms) return;

    // Update canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Calculate time values - use audio context time for better sync
    const audioContext = audioContextRef?.current;
    const currentTime = audioContext ? audioContext.currentTime : Date.now() / 1000;
    const elapsedTime = audioContext ? currentTime : (Date.now() - startTime) / 1000.0;
    const deltaTime = lastTime > 0 ? (currentTime - lastTime) : 0;
    setLastTime(currentTime);

    // Update uniforms
    gl.uniform3f(uniforms.iResolution, canvas.width, canvas.height, 1.0);
    gl.uniform1f(uniforms.iTime, elapsedTime);
    gl.uniform1f(uniforms.iTimeDelta, deltaTime);
    gl.uniform1i(uniforms.iFrame, frameCount);
    gl.uniform4f(uniforms.iMouse, 0, 0, 0, 0); // Mouse support can be added later
    
    // Date uniform (year, month, day, seconds)
    const date = new Date();
    const seconds = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
    gl.uniform4f(uniforms.iDate, date.getFullYear(), date.getMonth(), date.getDate(), seconds);

    // Audio uniforms - ensure they're being passed correctly
    const bass = audioData.bass || 0;
    const mid = audioData.mid || 0;
    const treble = audioData.treble || 0;
    const volume = audioData.volume || 0;
    
    // Check if uniforms exist before setting them
    if (uniforms.iAudioBass) {
      gl.uniform1f(uniforms.iAudioBass, bass);
    } else {
      console.warn('iAudioBass uniform not found in shader');
    }
    if (uniforms.iAudioMid) {
      gl.uniform1f(uniforms.iAudioMid, mid);
    } else {
      console.warn('iAudioMid uniform not found in shader');
    }
    if (uniforms.iAudioTreble) {
      gl.uniform1f(uniforms.iAudioTreble, treble);
    } else {
      console.warn('iAudioTreble uniform not found in shader');
    }
    if (uniforms.iAudioVolume) {
      gl.uniform1f(uniforms.iAudioVolume, volume);
    } else {
      console.warn('iAudioVolume uniform not found in shader');
    }

    // Custom parameters with audio mappings
    Object.keys(customParameters).forEach(paramName => {
      const uniformLocation = uniforms[paramName];
      if (!uniformLocation) return;

      let value = customParameters[paramName];
      
      // Apply audio mappings
      const mapping = audioMappings.find(m => m.parameterId === paramName);
      if (mapping) {
        const audioValue = audioData[mapping.audioSource] || 0;
        const modulation = audioValue * mapping.intensity;
        
        if (Array.isArray(value)) {
          // For vector types, apply modulation to all components
          const modulated = value.map(v => v + modulation);
          if (value.length === 2) {
            gl.uniform2f(uniformLocation, modulated[0], modulated[1]);
          } else if (value.length === 3) {
            gl.uniform3f(uniformLocation, modulated[0], modulated[1], modulated[2]);
          } else if (value.length === 4) {
            gl.uniform4f(uniformLocation, modulated[0], modulated[1], modulated[2], modulated[3]);
          }
        } else {
          // For scalar types
          const modulatedValue = mapping.offset + modulation;
          gl.uniform1f(uniformLocation, modulatedValue);
        }
      } else {
        // No audio mapping, use static value
        if (Array.isArray(value)) {
          if (value.length === 2) {
            gl.uniform2f(uniformLocation, value[0], value[1]);
          } else if (value.length === 3) {
            gl.uniform3f(uniformLocation, value[0], value[1], value[2]);
          } else if (value.length === 4) {
            gl.uniform4f(uniformLocation, value[0], value[1], value[2], value[3]);
          }
        } else {
          gl.uniform1f(uniformLocation, value);
        }
      }
    });

    // Reduced debug logging - only log errors
    if (frameCount % 300 === 0) { // Only every 5 seconds instead of every second
      if (!uniforms.iAudioBass || !uniforms.iAudioMid || !uniforms.iAudioTreble || !uniforms.iAudioVolume) {
        console.warn('Missing audio uniforms');
      }
    }

    // Render
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    setFrameCount(prev => prev + 1);

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(render);
    }
  };

  useEffect(() => {
    // console.log('Reinitializing WebGL due to shader or parameter changes');
    initWebGL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shaderCode, customParameters]); // Intentionally excluding initWebGL to prevent re-creation loops

  // Clear the canvas to black when not playing
  const clearCanvas = () => {
    const gl = glRef.current;
    const canvas = canvasRef.current;
    if (!gl || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    gl.viewport(0, 0, canvas.width, canvas.height);

    gl.clearColor(0, 0, 0, 1); // Black
    gl.clear(gl.COLOR_BUFFER_BIT);
  };

  useEffect(() => {
    if (isPlaying) {
      render();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Clear canvas to black when not playing
      clearCanvas();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, audioData]); // Intentionally excluding render function to prevent infinite loops

  // Trigger renders when parameters change
  useEffect(() => {
    if (glRef.current && programRef.current && isPlaying) {
      // Only render when playing - no single frame renders when stopped
      // The continuous render loop will pick up the changes automatically
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customParameters, audioMappings, isPlaying]); // Intentionally excluding render function to prevent infinite loops

  if (error) {
    return (
      <div className="absolute inset-0 w-full h-full flex items-center justify-center text-white bg-red-900 bg-opacity-50">
        <div className="text-center p-4">
          <div className="text-lg font-bold mb-2">Shadertoy Renderer Error</div>
          <div className="text-sm font-mono bg-black bg-opacity-50 p-2 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 w-full h-full" data-name="shadertoy-renderer">
      <canvas 
        ref={canvasRef}
        className="block w-full h-full pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      />
      {!isWebGL2 && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <div className="text-center">
            <div>Shadertoy Renderer</div>
            <div className="text-sm opacity-70">Initializing WebGL 2.0...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShadertoyRenderer;